import { useMemo } from 'react';
import {
  streamScenarioChat,
  regenerateScenarioChat,
  getScenarioChatHistory,
  clearScenarioChatConversation,
  postChatTelemetry,
  postChatFeedback,
  SCENARIO_CHAT_QUICK_ACTIONS,
} from '../services/loanChatApi';
import usePiperChat from './usePiperChat';

/**
 * Quick Pricer scenario-scoped Piper chat.
 * `getScenarioSnapshot` must return `{ pricingRequest, lastRunSummary }` from live form state.
 */
export default function useScenarioChat({
  enabled,
  isOpen,
  getScenarioSnapshot,
  quickActionsEnabled = true,
  quickActionsExpanded = false,
}) {
  const transport = useMemo(() => {
    if (!enabled) return null;

    const withSnapshot = (fn) => async (args) => {
      const snap = getScenarioSnapshot?.() || {};
      if (!snap.pricingRequest) {
        throw new Error('Set the scenario and run pricing before chatting with Piper.');
      }
      return fn({ ...args, pricingRequest: snap.pricingRequest, lastRunSummary: snap.lastRunSummary });
    };

    return {
      scopeKey: 'scenario',
      clientPresets: SCENARIO_CHAT_QUICK_ACTIONS,
      streamChat: withSnapshot(({ message, pricingRequest, lastRunSummary, token, signal, onContent, onStatus, onMessageId }) =>
        streamScenarioChat({
          message,
          pricingRequest,
          lastRunSummary,
          token,
          signal,
          onContent,
          onStatus,
          onMessageId,
        })),
      regenerate: withSnapshot(({ pricingRequest, lastRunSummary, token, signal, onContent, onStatus, onMessageId }) =>
        regenerateScenarioChat({
          pricingRequest,
          lastRunSummary,
          token,
          signal,
          onContent,
          onStatus,
          onMessageId,
        })),
      getHistory: (token) => getScenarioChatHistory(token),
      clear: (token) => clearScenarioChatConversation(token),
      telemetry: ({ eventType, intent, presetKey, conversationKey, metadata, token }) =>
        postChatTelemetry({
          scope: 'scenario',
          eventType,
          intent,
          presetKey,
          conversationKey,
          metadata,
          token,
        }),
      feedback: ({ conversationKey, messageId, rating, token }) =>
        postChatFeedback({
          scope: 'scenario',
          conversationKey,
          messageId,
          rating,
          token,
        }),
    };
  }, [enabled, getScenarioSnapshot]);

  return usePiperChat({
    transport,
    isOpen: Boolean(isOpen && enabled),
    quickActionsEnabled,
    quickActionsExpanded,
  });
}
